import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, X, QrCode, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useOverlayLock } from "@/contexts/UiOverlayContext";
import {
  useGenerateTradeQR,
  useTradeSession,
  useCancelTradeSession,
  formatPin,
  tradeErrorKey,
} from "@/lib/trade";
import { useI18n } from "@/lib/I18nContext";
import TradeMirror from "@/components/TradeMirror";

/**
 * Phase 17b — Generator side. Requests a 5-minute JWT + PIN for a target
 * user; renders QR + PIN; polls session status; when the counterpart
 * scans, swaps modal body inline to <TradeMirror perspective="generator" />.
 *
 * Zero explicit CSS `animation:` rules — motion-reduce (ASL) safe.
 */
export default function TradeGenerateModal({ open, onOpenChange, target }) {
  useOverlayLock(open);
  const { t } = useI18n();
  const generate = useGenerateTradeQR();
  const cancel = useCancelTradeSession();

  const [session, setSession] = useState(null); // { session_id, token, pin, expires_at, duplicates, missing }
  const [errKey, setErrKey] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [matchedSession, setMatchedSession] = useState(null); // TradeMirror-shaped
  const requestedRef = useRef(false);

  // Kick off generation once per open.
  useEffect(() => {
    if (!open || !target?.user_id) return;
    if (requestedRef.current) return;
    requestedRef.current = true;
    setErrKey(null);
    generate.mutate(
      { target_user_id: target.user_id },
      {
        onSuccess: (data) => setSession(data),
        onError: (err) => setErrKey(tradeErrorKey(err)),
      }
    );
  }, [open, target?.user_id, generate]);

  // Reset when the modal closes.
  useEffect(() => {
    if (open) return;
    requestedRef.current = false;
    setSession(null);
    setErrKey(null);
    setMatchedSession(null);
  }, [open]);

  // 1 Hz clock for the countdown.
  useEffect(() => {
    if (!open || !session) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open, session]);

  // Poll session status until it flips to "matched". Stop polling on match.
  const shouldPoll =
    !!session?.session_id && !matchedSession && open;
  const poll = useTradeSession(session?.session_id, {
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? 1500 : false,
  });

  // Auto-transition to TradeMirror the moment the counterpart scans.
  useEffect(() => {
    if (!poll.data) return;
    if (poll.data.status === "matched" && !matchedSession) {
      const dupes = poll.data.duplicates || [];
      const missing = poll.data.missing || [];
      setMatchedSession({
        session_id: poll.data.session_id,
        generator_perspective: { have: dupes, want: missing },
        scanner_perspective: { have: missing, want: dupes },
      });
      // Phase 18 — ASL feedback (green pulse for the generator).
      // Always dispatched; AslFeedbackBorder decides whether to render.
      window.dispatchEvent(
        new CustomEvent("fm:asl_feedback", { detail: { type: "success" } })
      );
    }
  }, [poll.data, matchedSession]);

  const expiresMs = useMemo(() => {
    if (!session?.expires_at) return 0;
    const t0 = Date.parse(session.expires_at);
    return Math.max(0, t0 - now);
  }, [session, now]);

  const secondsLeft = Math.floor(expiresMs / 1000);
  const isExpired = session && expiresMs === 0;

  // Phase 18 — fire a single error pulse the moment the session flips to
  // expired. `firedExpiryRef` guarantees exactly one dispatch per session.
  const firedExpiryRef = useRef(false);
  useEffect(() => {
    if (!session) {
      firedExpiryRef.current = false;
      return;
    }
    if (isExpired && !firedExpiryRef.current) {
      firedExpiryRef.current = true;
      window.dispatchEvent(
        new CustomEvent("fm:asl_feedback", { detail: { type: "error" } })
      );
    }
  }, [session, isExpired]);

  const handleClose = useCallback(() => {
    // If we generated but never matched, fire cancel silently.
    if (session?.session_id && !matchedSession) {
      cancel.mutate({ session_id: session.session_id });
    }
    onOpenChange(false);
  }, [session, matchedSession, cancel, onOpenChange]);

  // ── Rendering ──────────────────────────────────────────────
  // If matched, replace the modal body with TradeMirror inline
  // (same Dialog shell stays mounted → no flash / focus loss).
  if (matchedSession) {
    return (
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) onOpenChange(false);
        }}
      >
        <DialogContent
          data-testid="trade-generate-modal-matched"
          className="p-0 border-none bg-transparent max-w-none w-screen h-screen sm:rounded-none [&>button:last-child]:hidden"
        >
          <TradeMirror
            perspective="generator"
            session={matchedSession}
            onClose={() => onOpenChange(false)}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent
        data-testid="trade-generate-modal"
        className="bg-[#0A0A0A] border-white/10 text-white max-w-md p-0 overflow-hidden [&>button:last-child]:hidden"
      >
        <DialogHeader className="px-6 pt-6 pb-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-[#E5B83A]/15 border border-[#E5B83A]/30 flex items-center justify-center">
              <QrCode className="h-4 w-4 text-[#E5B83A]" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="font-display text-lg font-bold text-white truncate">
                {t("trade.generate_title")}
              </DialogTitle>
              <DialogDescription className="text-[#A1A1AA] font-body text-xs">
                {target?.display_name
                  ? t("trade.generate_subtitle_with_name").replace(
                      "{name}",
                      target.display_name
                    )
                  : t("trade.generate_subtitle")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 pb-6 space-y-5">
          {/* Loading */}
          {generate.isPending && !session ? (
            <div
              data-testid="trade-generate-loading"
              className="flex flex-col items-center justify-center py-14"
            >
              <Loader2 className="h-8 w-8 text-[#E5B83A] animate-spin" />
              <p className="mt-3 text-[#A1A1AA] font-body text-sm">
                {t("trade.generating")}
              </p>
            </div>
          ) : errKey ? (
            <ErrorPanel
              errKey={errKey}
              onRetry={() => {
                requestedRef.current = false;
                setErrKey(null);
              }}
              onClose={handleClose}
            />
          ) : session ? (
            <>
              {/* QR panel */}
              <div
                data-testid="trade-generate-qr-panel"
                className="rounded-xl bg-white p-5 flex items-center justify-center"
              >
                {isExpired ? (
                  <ExpiredBadge label={t("trade.expired")} />
                ) : (
                  <QRCodeSVG
                    data-testid="trade-generate-qr-svg"
                    value={session.token}
                    size={224}
                    level="M"
                    includeMargin={false}
                    fgColor="#000000"
                    bgColor="#FFFFFF"
                  />
                )}
              </div>

              {/* Phase 18 — Prominent PIN block, centered.
                  Label + PIN (≥48px) between QR and countdown. */}
              <div className="flex flex-col items-center gap-2">
                <p
                  data-testid="trade-generate-pin-label"
                  className="font-body text-xs uppercase tracking-widest text-amber-400/80"
                >
                  {t("trade.pairing_pin_label")}
                </p>
                <p
                  data-testid="trade-generate-pin"
                  className="font-mono font-bold text-white leading-none"
                  style={{
                    fontSize: "clamp(48px, 6vw, 72px)",
                    letterSpacing: "0.1em",
                  }}
                >
                  {formatPin(session.pin)}
                </p>
              </div>

              {/* Countdown row — centered under the PIN */}
              <div className="flex items-center justify-center gap-2">
                <p className="font-body text-[10px] uppercase tracking-wider text-[#A1A1AA]">
                  {t("trade.expires_in")}
                </p>
                <p
                  data-testid="trade-generate-countdown"
                  className={
                    "font-mono font-bold text-2xl tabular-nums " +
                    (secondsLeft <= 30 ? "text-[#FF6B6B]" : "text-[#E5B83A]")
                  }
                >
                  {formatCountdown(secondsLeft)}
                </p>
              </div>

              {/* Waiting hint */}
              <p
                data-testid="trade-generate-waiting"
                className="text-[#A1A1AA] font-body text-xs text-center leading-relaxed"
              >
                {t("trade.waiting_for_scan")}
              </p>

              {/* Trade preview counts */}
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="rounded-md border border-[#E5B83A]/30 bg-[#E5B83A]/5 py-2">
                  <p className="font-body text-[10px] uppercase tracking-wider text-[#E5B83A]">
                    {t("trade.you_give_short")}
                  </p>
                  <p
                    data-testid="trade-generate-give-count"
                    className="font-display font-bold text-white text-lg"
                  >
                    {session.duplicates?.length || 0}
                  </p>
                </div>
                <div className="rounded-md border border-white/10 bg-white/5 py-2">
                  <p className="font-body text-[10px] uppercase tracking-wider text-[#A1A1AA]">
                    {t("trade.you_get_short")}
                  </p>
                  <p
                    data-testid="trade-generate-get-count"
                    className="font-display font-bold text-white text-lg"
                  >
                    {session.missing?.length || 0}
                  </p>
                </div>
              </div>
            </>
          ) : null}
        </div>

        {/* Sticky footer with cancel — separate from close-X for clarity */}
        <div className="px-6 pb-6 pt-2 border-t border-white/5 flex items-center justify-end gap-2">
          {isExpired && session ? (
            <button
              type="button"
              data-testid="trade-generate-retry"
              onClick={() => {
                requestedRef.current = false;
                setSession(null);
                setErrKey(null);
              }}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#E5B83A]/40 text-[#E5B83A] font-body text-sm hover:bg-[#E5B83A]/10"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {t("buttons.retry")}
            </button>
          ) : null}
          <button
            type="button"
            data-testid="trade-generate-cancel"
            onClick={handleClose}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-white/10 text-white font-body text-sm hover:bg-white/5"
          >
            <X className="h-3.5 w-3.5" />
            {t("trade.cancel")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatCountdown(secondsLeft) {
  if (secondsLeft <= 0) return "00:00";
  const m = Math.floor(secondsLeft / 60);
  const s = secondsLeft % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function ExpiredBadge({ label }) {
  return (
    <div className="w-56 h-56 flex flex-col items-center justify-center text-center">
      <p className="font-display font-black text-2xl text-black">{label}</p>
    </div>
  );
}

function ErrorPanel({ errKey, onRetry, onClose }) {
  const { t } = useI18n();
  const msg = t(`trade.err.${errKey || "generic"}`);
  return (
    <div
      data-testid="trade-generate-error"
      className="py-8 text-center space-y-4"
    >
      <p className="font-body text-sm text-[#FF6B6B]">{msg}</p>
      <div className="flex items-center justify-center gap-2">
        <button
          type="button"
          data-testid="trade-generate-error-retry"
          onClick={onRetry}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-[#E5B83A]/40 text-[#E5B83A] font-body text-xs hover:bg-[#E5B83A]/10"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {t("buttons.retry")}
        </button>
        <button
          type="button"
          data-testid="trade-generate-error-close"
          onClick={onClose}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-white/10 text-white font-body text-xs hover:bg-white/5"
        >
          {t("buttons.close")}
        </button>
      </div>
    </div>
  );
}
